import { useLanguage } from "../../../contexts/LanguageContext";
import { timeZoneLabel } from "../../../utils/timeZones";
import { formatNumber, weekdayName } from "../format";
import { EmptyNote, Panel, Skeleton } from "./ui";

// One hue, light to dark (sequential): more visits, darker cell.
const SCALE = ["#F3F4F6", "#E0E7FF", "#A5B4FC", "#6366F1", "#3730A3"];
// Rows start on Monday; dow is 0 = Sunday as the API sends it.
const ROW_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DEFAULT_HOURS = [9, 20];

export default function Heatmap({ cells, timeZone, loading }) {
  const { t, language } = useLanguage();
  const list = Array.isArray(cells) ? cells : [];
  const withVisits = list.filter((c) => c.visits > 0);

  const [first, last] = withVisits.length
    ? [Math.min(...withVisits.map((c) => c.hour)), Math.max(...withVisits.map((c) => c.hour))]
    : DEFAULT_HOURS;
  const hours = [];
  for (let h = first; h <= last; h += 1) hours.push(h);

  const lookup = new Map(list.map((c) => [`${c.dow}-${c.hour}`, c.visits || 0]));
  const max = Math.max(0, ...withVisits.map((c) => c.visits));
  const level = (v) => (v <= 0 || max === 0 ? 0 : Math.min(4, Math.ceil((v / max) * 4)));
  const peak = withVisits.reduce((best, c) => (!best || c.visits > best.visits ? c : best), null);
  const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

  return (
    <Panel
      title={t("dashboard.insights.heatmap.title")}
      subtitle={t("dashboard.insights.heatmap.subtitle", {
        timeZone: timeZoneLabel(timeZone || "UTC"),
      })}
      className="flex-[2_1_560px]"
      actions={
        <div className="flex items-center gap-1.5 text-xs text-gray-600" aria-hidden="true">
          <span>{t("dashboard.insights.heatmap.fewer")}</span>
          {SCALE.map((c) => (
            <span key={c} className="h-3.5 w-3.5 rounded-[3px]" style={{ background: c }} />
          ))}
          <span>{t("dashboard.insights.heatmap.more")}</span>
        </div>
      }
    >
      {loading ? (
        <Skeleton className="h-56 w-full" />
      ) : withVisits.length === 0 ? (
        <EmptyNote />
      ) : (
        <>
          <div className="relative overflow-x-auto">
            <table className="w-full min-w-[560px] border-separate border-spacing-1 text-xs">
              <thead>
                <tr>
                  <th scope="col" className="w-10">
                    <span className="sr-only">{t("dashboard.insights.heatmap.day")}</span>
                  </th>
                  {hours.map((h) => (
                    <th key={h} scope="col" className="font-normal text-gray-500">
                      {hourLabel(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROW_ORDER.map((dow) => (
                  <tr key={dow}>
                    <th scope="row" className="pr-1 text-left font-normal text-gray-600">
                      {weekdayName(dow, language)}
                    </th>
                    {hours.map((h) => {
                      const v = lookup.get(`${dow}-${h}`) || 0;
                      const label = t("dashboard.insights.heatmap.cell", {
                        day: weekdayName(dow, language, "long"),
                        hour: hourLabel(h),
                        value: formatNumber(v, language),
                      });
                      return (
                        <td
                          key={h}
                          title={label}
                          className="h-7 rounded-[5px]"
                          style={{ background: SCALE[level(v)] }}
                        >
                          <span className="sr-only">{label}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {peak && (
            <p className="text-[13px] text-gray-700">
              {t("dashboard.insights.heatmap.peak", {
                day: weekdayName(peak.dow, language, "long"),
                hour: hourLabel(peak.hour),
              })}
            </p>
          )}
        </>
      )}
    </Panel>
  );
}
